import { useManualTenderDrawer } from '@/lib/services/cash-drawers/useManualTenderDrawer'
import { InfoNotice } from '@/components/patterns/info-notice'
import { refreshManualTenderDrawer } from '@/lib/services/cash-drawers/cash-drawers.preflight'
import { useAbility } from '@/lib/casl'
import { useOpenCashDrawers } from '@/lib/services/cash-drawers/cash-drawers.query'
import { useRouteOrganizationIdParam } from '@/hooks/useRouteOrganizationId'
import { requireOrganizationId } from '@/lib/organization-scoping'
import type {
  SalesOrderCancellationStatus,
  SalesOrderIdInput,
} from '@/lib/services/sales-orders/sales-orders.types'
import type { CashDrawerIdInput } from '@/lib/services/cash-drawers/cash-drawers.types'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import {
  useCancellationStatus,
  useRetryCancellationRefundMutation,
} from '@/lib/services/sales-orders/sales-orders.query'
import { formatCurrency, getErrorMessage } from '@/lib/utils'
import { toast } from 'sonner'

type CancellationRefundNoticeProps = {
  salesOrderId: SalesOrderIdInput
  initialStatus?: Parameters<typeof useCancellationStatus>[0]['initialStatus']
  canManage?: boolean
  locationId?: CashDrawerIdInput
}

// An empty cancellation response cannot carry the server's retry flag.
function hasRetryableCancellation(
  status: ReturnType<typeof useCancellationStatus>['data']
): status is SalesOrderCancellationStatus {
  return Boolean(status?.can_retry)
}

export default function CancellationRefundNotice({
  salesOrderId,
  initialStatus,
  canManage,
  locationId,
}: CancellationRefundNoticeProps) {
  const query = useCancellationStatus({ salesOrderId, initialStatus })
  const retry = useRetryCancellationRefundMutation()
  const status = query.data
  const orgId = useRouteOrganizationIdParam()
  const ability = useAbility()
  const canOperateCashDrawers = ability?.can('operate', 'cash-drawers') === true
  const needsManualRefund = status?.requires_manual_refund === true
  const {
    statusQuery,
    trackingEnabled,
    workstationsQuery,
    workstationId,
    pairedWorkstation,
    currentDrawerQuery,
  } = useManualTenderDrawer({
    organizationId: orgId,
    targetLocationId: locationId,
    canOperateCashDrawers,
    enabled: canManage && needsManualRefund,
  })
  const openDrawersQuery = useOpenCashDrawers({
    enabled: canManage && needsManualRefund && trackingEnabled && canOperateCashDrawers,
  })
  const [isPreparing, setIsPreparing] = useState(false)
  const isRetrying = isPreparing || retry.isPending
  const needsAttention = status?.status === 'needs_attention'
  if (status?.status === 'completed' || status?.status === 'not_required') return null
  if (!needsAttention && status?.status !== 'processing' && !query.isError) return null

  async function retryRefund(cancellation: SalesOrderCancellationStatus) {
    setIsPreparing(true)
    try {
      let cashDrawerSessionId
      if (needsManualRefund) {
        const context = await refreshManualTenderDrawer({
          allowSoleOpenSession: true,
          openDrawersQuery,
          canOperateCashDrawers,
          currentDrawerQuery,
          organizationId: requireOrganizationId(orgId),
          pairedWorkstation,
          statusQuery,
          targetLocationId: locationId,
          workstationId,
          workstationsQuery,
        })
        if (context.trackingEnabled) cashDrawerSessionId = context.session.id
      }
      const response = await retry.mutateAsync({
        salesOrderId,
        refundAttempt: cancellation.refund_attempt,
        ...(cashDrawerSessionId ? { cashDrawerSessionId } : {}),
      })
      const notify =
        response.cancellation?.status === 'needs_attention' ? toast.warning : toast.success
      notify(response.message)
    } catch (error) {
      toast.error('Unable to retry the refund', { description: getErrorMessage(error) })
    } finally {
      setIsPreparing(false)
    }
  }

  return (
    <InfoNotice
      title={
        query.isError
          ? 'Refund status unavailable'
          : needsAttention
            ? 'Refund needs attention'
            : 'Refund processing'
      }
      tone={needsAttention ? 'warning' : 'info'}
    >
      <div className="space-y-3">
        <Typography variant="body">
          This order is cancelled and will not be fulfilled.
          {query.isError
            ? ' Check the refund status before taking another action.'
            : needsAttention
              ? ` ${formatCurrency(status.failed_total)} still needs to be returned to the customer.`
              : ' The refund is still processing. Do not submit another refund while it is pending.'}
        </Typography>
        {needsAttention && !status.can_retry && canManage ? (
          <Typography variant="supporting">
            Contact support to review the existing refund before trying again.
          </Typography>
        ) : null}
        {query.isError ? (
          <Typography variant="supporting" tone="destructive">
            Unable to refresh the refund status. No new refund has been submitted.
          </Typography>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={query.isFetching || isRetrying}
            onClick={() => query.refetch()}
          >
            Check status
          </Button>
          {canManage && hasRetryableCancellation(status) ? (
            <Button
              size="sm"
              disabled={isRetrying || query.isFetching || query.isError}
              onClick={() => retryRefund(status)}
            >
              {isRetrying ? 'Retrying…' : `Retry ${formatCurrency(status.failed_total)} refund`}
            </Button>
          ) : null}
        </div>
      </div>
    </InfoNotice>
  )
}
